import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { parseAbiItem, type Address, type Log } from "viem";
import type { RedKeel } from "../config/networks";
import { clienteLogs, logsConVentanas } from "../lib/cliente";

const OPENED = parseAbiItem("event Opened(uint256 costBasisRef, int24 openTick)");

/**
 * Cuándo se abrió la posición que está viva AHORA.
 *
 * Se toma el ÚLTIMO `Opened`, no el primero: un vault se puede vaciar y volver a abrir, y entonces
 * «lleva funcionando» es desde la apertura de esta posición, no desde que el contrato existe. Con el
 * primero, un vault reabierto ayer diría que lleva tres meses trabajando.
 *
 * Es una consulta aparte y no parte de `useVault` a propósito: `useVault` se refresca cada 30 s y
 * esto no cambia nunca mientras la posición siga abierta.
 */
export function useApertura(direccion: Address | null, red: RedKeel | undefined, abierto: boolean) {
  return useQuery({
    queryKey: ["apertura", red?.clave, direccion],
    enabled: !!direccion && !!red && abierto,
    // NO `Infinity`: un fallo transitorio del RPC de eventos (base.org rate-limitea bajo la ráfaga de
    // la ficha) devolvía [] → null, y con caché infinita se quedaba PEGADO como "no encuentro la
    // apertura" para siempre, aunque el evento SÍ estuviera on-chain. Con caché finita + reintentos +
    // LANZAR cuando no lo lee (en un vault ABIERTO siempre hay `Opened`), se reintenta y se auto-cura.
    staleTime: 5 * 60_000,
    retry: 4,
    retryDelay: (i) => Math.min(1200 * (i + 1), 6000),
    queryFn: async (): Promise<number> => {
      // Por el RPC de EVENTOS, no por el de lectura: ver la nota de `clienteLogs`. Y con ventanas,
      // porque BNB Chain no deja mirar toda la cadena de una vez.
      const client = clienteLogs(red!);
      const logs = await logsConVentanas<Log<bigint, number, false, typeof OPENED>>(
        client,
        { address: direccion!, event: OPENED },
        red!
      );
      // Un vault ABIERTO tiene por fuerza un `Opened`. Si no salió, la lectura falló (rate-limit, hipo
      // del RPC) → se LANZA para que react-query reintente, en vez de cachear un "no encuentro" falso.
      if (!logs.length) throw new Error("no pude leer la apertura todavía (reintentando)");
      const ultimo = logs.reduce((a, b) => (b.blockNumber > a.blockNumber ? b : a));
      const bloque = await client.getBlock({ blockNumber: ultimo.blockNumber }); // que lance si falla → reintenta
      return Number(bloque.timestamp);
    },
  });
}

/**
 * Cuándo se abrió CADA UNA de las piscinas de la lista, en una sola consulta.
 *
 * `getLogs` acepta varias direcciones a la vez, y eso es lo que hace viable enseñar la antigüedad en
 * la lista: preguntar vault por vault serían quince idas y vueltas cada vez que se abre la pantalla,
 * y en Robinhood o HyperEVM es justo la forma de comerse un 429 y quedarse sin nada.
 *
 * Los tiempos que no se puedan leer sencillamente no salen: una fila sin antigüedad se entiende, una
 * lista que no carga porque un RPC se atragantó con un filtro, no.
 */
export type Apertura = {
  /** fecha (epoch s) del último `Opened`, o undefined si no se pudo leer el bloque. */
  abierta?: number;
  /** `costBasisRef` con el que ABRIÓ esta posición: el capital de verdad para el %. El de estado en
   *  vivo baja al retirar principal, y dividir el ganado de toda la vida por lo que queda dispara
   *  el porcentaje (visto +260% con capital vivo de 1,73 en un vault que abrió con 195). */
  capital: bigint;
};

export function useAperturas(direcciones: Address[], red: RedKeel | undefined) {
  const clave = [...direcciones].sort().join(",");

  return useQuery({
    queryKey: ["aperturas", red?.clave, clave],
    enabled: !!red && direcciones.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Record<string, Apertura>> => {
      const client = clienteLogs(red!);
      // `acumula`: sin él, en redes troceadas (HyperEVM) se devuelve solo la ventana más reciente y
      // los vaults que abrieron antes se quedan sin fecha —«Trabajando —»— aunque su `Opened` sea
      // perfectamente legible unas ventanas más atrás.
      const logs = await logsConVentanas<Log<bigint, number, false, typeof OPENED>>(
        client,
        { address: direcciones, event: OPENED },
        red!,
        { acumula: true }
      );
      if (!logs.length) return {};

      // Del último `Opened` de cada vault: una piscina se puede vaciar y reabrir, y lo que se cuenta
      // es lo que lleva viva ESTA posición, no el contrato. Se guarda su bloque Y su `costBasisRef`.
      const ultimo = new Map<string, { block: bigint; capital: bigint }>();
      for (const l of logs) {
        const d = l.address.toLowerCase();
        const b = l.blockNumber ?? 0n;
        if (b > (ultimo.get(d)?.block ?? -1n)) ultimo.set(d, { block: b, capital: l.args?.costBasisRef ?? 0n });
      }

      // Un `getBlock` por bloque DISTINTO, no por vault: varias piscinas abiertas el mismo día
      // suelen compartir bloque, y aunque no lo compartan son bastantes menos peticiones que filas.
      const bloques = [...new Set([...ultimo.values()].map((v) => v.block))];
      const fechas = new Map<bigint, number>();
      await Promise.all(
        bloques.map((b) =>
          client
            .getBlock({ blockNumber: b })
            .then((x) => fechas.set(b, Number(x.timestamp)))
            .catch(() => undefined)
        )
      );

      const salida: Record<string, Apertura> = {};
      for (const [dir, { block, capital }] of ultimo) {
        salida[dir] = { abierta: fechas.get(block), capital };
      }
      return salida;
    },
  });
}

/**
 * Un reloj que corre en la pantalla.
 *
 * Devuelve los segundos transcurridos desde `desde`, y se refresca solo. El intervalo se ajusta a lo
 * que de verdad cambia: cada segundo mientras se enseñen segundos, y cada 20 s cuando ya solo se
 * ven minutos — repintar una cifra que no cambia 60 veces por minuto es gastar batería por nada.
 */
export function useSegundosDesde(desde: number | null | undefined) {
  const [ahora, setAhora] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    if (!desde) return;
    const paso = Math.floor(Date.now() / 1000) - desde < 3600 ? 1000 : 20_000;
    const id = setInterval(() => setAhora(Math.floor(Date.now() / 1000)), paso);
    return () => clearInterval(id);
  }, [desde]);

  return desde ? Math.max(0, ahora - desde) : null;
}

/**
 * "3 días 14 h 22 min". Baja de unidad según hace falta, y NUNCA enseña tres unidades cuando la
 * tercera no aporta: a los cuatro días, los segundos son ruido que además hace parpadear la cifra.
 */
export function duracion(s: number): string {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const seg = s % 60;

  if (d > 0) return `${d} ${d === 1 ? "día" : "días"} ${h} h ${m} min`;
  if (h > 0) return `${h} h ${m} min`;
  if (m > 0) return `${m} min ${String(seg).padStart(2, "0")} s`;
  return `${seg} s`;
}

/** "desde el 12 de agosto a las 16:07" */
export function desdeCuando(ts: number): string {
  const d = new Date(ts * 1000);
  return `desde el ${d.toLocaleDateString("es", { day: "numeric", month: "long" })} a las ${d.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}`;
}
